import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import {
  createCustomerPortalSupplier,
  getCustomerPortalSuppliers,
  updateCustomerPortalSupplier,
  toPortalErrorMessage,
} from "../../services/customerPortalApi";

const EMPTY_FORM = {
  name: "",
  supplierType: "supplier",
  contactName: "",
  email: "",
  phone: "",
  notes: "",
};
const DEFAULT_LIMIT = 25;

export default function PortalSuppliersPage() {
  const { user } = useUser();
  const accountType = String(user?.accountType || "internal").trim().toLowerCase();
  const portalRole = String(user?.customerPortalRole || "").trim().toLowerCase();
  const isCustomerPortalUser = accountType === "customer_portal";
  const canWriteSupplier = isCustomerPortalUser && ["owner", "manager"].includes(portalRole);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [query, setQuery] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const limit = DEFAULT_LIMIT;

  useEffect(() => {
    let active = true;

    async function load() {
      if (!isCustomerPortalUser) {
        setItems([]);
        setTotal(0);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const response = await getCustomerPortalSuppliers({
          page,
          limit,
          includeInactive: true,
          q: String(query || "").trim() || undefined,
        });
        if (!active) return;
        setItems(Array.isArray(response?.items) ? response.items : []);
        setTotal(Number(response?.total || 0));
      } catch (err) {
        if (!active) return;
        setError(toPortalErrorMessage(err, "Leveranciers laden mislukt"));
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [isCustomerPortalUser, limit, page, query, refreshKey]);

  const pagination = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / limit));
    const safePage = Math.min(Math.max(1, page), totalPages);
    return {
      page: safePage,
      totalPages,
      hasPrevious: safePage > 1,
      hasNext: safePage < totalPages,
    };
  }, [limit, page, total]);

  useEffect(() => {
    if (page > pagination.totalPages) {
      setPage(pagination.totalPages);
    }
  }, [page, pagination.totalPages]);

  async function handleSubmit(event) {
    event.preventDefault();
    if (!canWriteSupplier || saving) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const payload = {
        name: String(form.name || "").trim(),
        supplierType: String(form.supplierType || "supplier").trim().toLowerCase(),
        contactName: String(form.contactName || "").trim(),
        email: String(form.email || "").trim(),
        phone: String(form.phone || "").trim(),
        notes: String(form.notes || "").trim(),
      };

      if (editingId) {
        await updateCustomerPortalSupplier(editingId, payload);
        setSuccess("Leverancier bijgewerkt.");
      } else {
        await createCustomerPortalSupplier(payload);
        setSuccess("Leverancier toegevoegd.");
      }
      setForm(EMPTY_FORM);
      setEditingId("");
      setPage(1);
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(toPortalErrorMessage(err, "Leverancier opslaan mislukt"));
    } finally {
      setSaving(false);
    }
  }

  function startEdit(item) {
    setEditingId(String(item?.id || item?._id || ""));
    setForm({
      name: String(item?.name || ""),
      supplierType: String(item?.supplierType || "supplier"),
      contactName: String(item?.contactName || ""),
      email: String(item?.email || ""),
      phone: String(item?.phone || ""),
      notes: String(item?.notes || ""),
    });
  }

  function cancelEdit() {
    setEditingId("");
    setForm(EMPTY_FORM);
  }

  async function toggleActive(item) {
    if (!canWriteSupplier) return;
    const supplierId = String(item?.id || item?._id || "");
    if (!supplierId) return;

    try {
      setSaving(true);
      setError("");
      setSuccess("");
      await updateCustomerPortalSupplier(supplierId, {
        active: item?.active === false,
      });
      setSuccess(item?.active === false ? "Leverancier geactiveerd." : "Leverancier gearchiveerd.");
      setRefreshKey((current) => current + 1);
    } catch (err) {
      setError(toPortalErrorMessage(err, "Leverancier status wijzigen mislukt"));
    } finally {
      setSaving(false);
    }
  }

  if (!isCustomerPortalUser) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
        <div className="mx-auto max-w-5xl space-y-4">
          <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <nav className="mb-2 text-xs text-slate-500">
              <Link to="/portaal" className="hover:underline">Dashboard</Link>
              <span className="px-1">/</span>
              <span className="font-semibold text-slate-700">Leveranciers</span>
            </nav>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
            <h1 className="text-2xl font-bold text-slate-900">Leveranciers</h1>
          </header>

          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
            Leveranciersbeheer is alleen beschikbaar voor externe klantportaalaccounts.
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-blue-50 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <header className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <nav className="mb-2 text-xs text-slate-500">
            <Link to="/portaal" className="hover:underline">Dashboard</Link>
            <span className="px-1">/</span>
            <span className="font-semibold text-slate-700">Leveranciers</span>
          </nav>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Klantportaal</p>
          <h1 className="text-2xl font-bold text-slate-900">Leveranciers</h1>
          <p className="text-sm text-slate-600">
            Beheer leveranciers voor jouw organisatie binnen dit portaal.
          </p>
          <p className="mt-2 text-xs text-slate-500">
            Jouw rol: <span className="font-semibold text-slate-700">{formatRoleLabel(portalRole)}</span>
          </p>
        </header>

        {error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div>
        )}
        {success && (
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {success}
          </div>
        )}

        {canWriteSupplier ? (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-base font-semibold text-slate-900">
              {editingId ? "Leverancier bewerken" : "Nieuwe leverancier"}
            </h2>
            <form className="mt-3 grid gap-3 sm:grid-cols-2" onSubmit={handleSubmit}>
              <label className="grid gap-1 text-sm text-slate-700">
                Naam
                <input
                  required
                  value={form.name}
                  onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </label>

              <label className="grid gap-1 text-sm text-slate-700">
                Type
                <select
                  value={form.supplierType}
                  onChange={(event) => setForm((current) => ({ ...current, supplierType: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                >
                  <option value="supplier">Leverancier</option>
                  <option value="zzp">ZZP</option>
                  <option value="subcontractor">Onderaannemer</option>
                </select>
              </label>

              <label className="grid gap-1 text-sm text-slate-700">
                Contactpersoon
                <input
                  value={form.contactName}
                  onChange={(event) => setForm((current) => ({ ...current, contactName: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </label>

              <label className="grid gap-1 text-sm text-slate-700">
                E-mail
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </label>

              <label className="grid gap-1 text-sm text-slate-700 sm:col-span-2">
                Telefoon
                <input
                  value={form.phone}
                  onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </label>

              <label className="grid gap-1 text-sm text-slate-700 sm:col-span-2">
                Notities
                <textarea
                  rows={3}
                  value={form.notes}
                  onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                  className="rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
                />
              </label>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {saving ? "Opslaan..." : editingId ? "Opslaan" : "Leverancier toevoegen"}
                </button>
                {editingId ? (
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="ml-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Annuleren
                  </button>
                ) : null}
              </div>
            </form>
          </section>
        ) : (
          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
            Je hebt alleen leestoegang. Vraag een portaalbeheerder met rol owner of manager om leveranciers toe te voegen.
          </section>
        )}

        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-900">Jouw leveranciers</h2>
            <input
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
              placeholder="Zoek op naam, contact, e-mail, telefoon"
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-blue-500 sm:w-80"
            />
          </div>

          <div className="mb-3 flex items-center justify-between gap-2 px-2 text-xs text-slate-600">
            <p>
              Pagina <span className="font-semibold text-slate-800">{pagination.page}</span> van{" "}
              <span className="font-semibold text-slate-800">{pagination.totalPages}</span> • Totaal:{" "}
              <span className="font-semibold text-slate-800">{total}</span>
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={!pagination.hasPrevious || loading || saving}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() =>
                  setPage((current) => Math.min(pagination.totalPages, current + 1))
                }
                disabled={!pagination.hasNext || loading || saving}
                className="rounded-lg border border-slate-300 px-2.5 py-1 font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-slate-500">Laden...</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-slate-500">Nog geen leveranciers gevonden.</p>
          ) : (
            <div className="space-y-2">
              <div className="grid gap-2 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500 md:grid-cols-[1.3fr_120px_160px_1fr_1fr_160px_190px]">
                <span>Naam</span>
                <span>Status</span>
                <span>Type</span>
                <span>Contact</span>
                <span>E-mail</span>
                <span>Telefoon</span>
                <span className="text-right">Acties</span>
              </div>
              {items.map((item) => (
                <article
                  key={String(item?.id || item?._id || "")}
                  className="grid gap-2 rounded-xl border border-slate-200 p-3 md:grid-cols-[1.3fr_120px_160px_1fr_1fr_160px_190px]"
                >
                  <p className="text-sm font-semibold text-slate-900">{item?.name || "-"}</p>
                  <p className="text-sm text-slate-700">{item?.active === false ? "Inactief" : "Actief"}</p>
                  <p className="text-sm text-slate-700">{item?.supplierTypeLabel || "-"}</p>
                  <p className="text-sm text-slate-700">{item?.contactName || "-"}</p>
                  <p className="text-sm text-slate-700">{item?.email || "-"}</p>
                  <p className="text-sm text-slate-700">{item?.phone || "-"}</p>
                  <div className="text-right">
                    {canWriteSupplier ? (
                      <>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => startEdit(item)}
                          className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        >
                          Bewerken
                        </button>
                        <button
                          type="button"
                          disabled={saving}
                          onClick={() => toggleActive(item)}
                          className="ml-2 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                        >
                          {item?.active === false ? "Activeer" : "Archiveer"}
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-slate-500">Alleen lezen</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function formatRoleLabel(role) {
  const key = String(role || "").trim().toLowerCase();
  if (key === "owner") return "Owner";
  if (key === "manager") return "Manager";
  return "Viewer";
}
